import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Box,
  Button,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

// Base URL for API (for debugging)
const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

export const TriggerSelector = ({ open, onClose, onSelect }) => {
  const [triggers, setTriggers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: ''
  });

  const fetchTriggers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/triggers`);
      const data = await response.json();
      setTriggers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching triggers:', error);
      setTriggers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchTriggers();
    }
  }, [open]);

  const handleCreateSubmit = async () => {
    try {
      if (!formData.name) {
        throw new Error('Name is required');
      }

      const response = await fetch(`${API_BASE_URL}/api/triggers`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save trigger');
      }

      const newTrigger = await response.json();
      setCreateDialogOpen(false);
      setFormData({ name: '', key: '', description: '' });
      await fetchTriggers();
      
      onSelect({ 
        id: newTrigger._id, 
        name: newTrigger.name,
        key: newTrigger.key
      });
    } catch (error) {
      console.error('Error saving trigger:', error);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Select Trigger
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => setCreateDialogOpen(true)}
              startIcon={<AddIcon />}
            >
              Add Trigger
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ padding: 1 }}>Name</TableCell>
                    <TableCell sx={{ padding: 1 }}>Key</TableCell>
                    <TableCell sx={{ padding: 1 }}>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {triggers.map((trigger) => (
                    <TableRow 
                      key={trigger._id}
                      sx={{ 
                        '&:hover': { bgcolor: 'action.hover' },
                        cursor: 'pointer'
                      }}
                      onClick={() => onSelect({ 
                        id: trigger._id, 
                        name: trigger.name,
                        key: trigger.key
                      })}
                    >
                      <TableCell sx={{ padding: 1 }}>
                        <Chip color="primary" label={trigger.name} />
                      </TableCell>
                      <TableCell sx={{ padding: 1 }}>
                        <Typography variant="body2" color="primary" fontFamily="monospace">
                          {trigger.key}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ padding: 1 }}>{trigger.description}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create trigger</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Key"
              value={formData.key}
              onChange={(e) => setFormData({ ...formData, key: e.target.value })}
              fullWidth
              required
              placeholder="unique_key_name"
              helperText="Unique identifier (e.g., 'player_joins', 'item_purchased')"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 